/* Vibed Store Locator client logic.
 *
 * Boot order:
 *   1. Section liquid loads google maps JS with callback=VibedLocatorBoot.
 *   2. Defer-loaded this script registers the global before maps fires.
 *   3. Maps invokes VibedLocatorBoot(); we fetch the JSON feed and render.
 */
(function () {
  'use strict';

  var cfg = window.VibedLocatorConfig || {};
  var mapEl, listEl, contextEl, emptyEl, paneEl, detailEl;
  var map = null;
  var infoWindow = null;
  var geocoder = null;
  var markers = []; // { id, marker, store }
  var stores = []; // raw store records
  var origin = null; // {lat,lng} of last search center
  var activeFlavors = new Set(); // empty = all flavors
  var activeCardEl = null;
  var selectedStoreId = null;
  var prevMapState = null; // {center, zoom} before zooming into a store

  function ready(fn) {
    if (document.readyState === 'loading') {
      document.addEventListener('DOMContentLoaded', fn);
    } else {
      fn();
    }
  }

  function $(id) { return document.getElementById(id); }

  function deg2rad(d) { return d * Math.PI / 180; }

  function haversine(a, b) {
    if (!a || !b) return Infinity;
    var R = 3959; // miles
    var dLat = deg2rad(b.lat - a.lat);
    var dLng = deg2rad(b.lng - a.lng);
    var lat1 = deg2rad(a.lat);
    var lat2 = deg2rad(b.lat);
    var x = Math.sin(dLat / 2) ** 2 +
            Math.sin(dLng / 2) ** 2 * Math.cos(lat1) * Math.cos(lat2);
    return 2 * R * Math.asin(Math.sqrt(x));
  }

  function formatDistance(mi) {
    if (mi == null || !isFinite(mi)) return '';
    if (mi < 0.1) return '< 0.1 mi';
    return mi.toFixed(1) + ' mi';
  }

  function directionsUrl(s) {
    var q = encodeURIComponent(
      [s.name, s.address, s.city, s.state, s.zip].filter(Boolean).join(', ')
    );
    return 'https://www.google.com/maps/dir/?api=1&destination=' + q;
  }

  function passesFlavor(s) {
    if (activeFlavors.size === 0) return true;
    var skus = s.skus || [];
    // OR semantics: store passes if it carries ANY of the selected flavors.
    for (var i = 0; i < skus.length; i++) {
      if (activeFlavors.has(skus[i])) return true;
    }
    return false;
  }

  // Radius filter is only applied when we have a search origin (entered
  // ZIP/city or auto-detected location). Without an origin we have no
  // anchor to measure from, so we fall back to showing everything.
  function passesRadius(s) {
    if (!origin) return true;
    var r = cfg.radiusMiles;
    if (!r || r <= 0) return true;
    return haversine(origin, { lat: s.lat, lng: s.lng }) <= r;
  }

  function passesAll(s) {
    return passesFlavor(s) && passesRadius(s);
  }

  function avatarInitial(name) {
    if (!name) return '?';
    // Strip leading punctuation/numbers so 'Pogo's' beats '#1 Liquor' getting '#'.
    var cleaned = name.replace(/^[^A-Za-z]+/, '');
    return (cleaned || name).trim().charAt(0).toUpperCase();
  }

  function isMobile() {
    return window.matchMedia('(max-width: 899px)').matches;
  }

  function renderList() {
    listEl.innerHTML = '';
    var matches = stores.filter(passesAll);
    if (origin) {
      matches.forEach(function (s) {
        s._dist = haversine(origin, { lat: s.lat, lng: s.lng });
      });
      matches.sort(function (a, b) { return a._dist - b._dist; });
    } else {
      matches.sort(function (a, b) { return a.name.localeCompare(b.name); });
    }

    var cardTpl = $('vibed-locator-card-tpl');
    var visibleCount = 0;
    var maxRender = 200;
    matches.slice(0, maxRender).forEach(function (s) {
      var node = cardTpl.content.cloneNode(true);
      var li = node.querySelector('.vibed-locator__card');
      li.dataset.id = s.id;

      // Avatar: circle with first initial of the retailer name. Real
      // logos go here later if we onboard recognizable chains.
      node.querySelector('.vibed-locator__card-avatar').textContent = avatarInitial(s.name);

      node.querySelector('.vibed-locator__card-name').textContent = s.name;
      var dist = origin ? formatDistance(s._dist) : '';
      node.querySelector('.vibed-locator__card-distance').textContent = dist;
      var addrEl = node.querySelector('.vibed-locator__card-addr');
      addrEl.textContent = '';
      var line1 = document.createElement('span');
      line1.textContent = s.address;
      line1.className = 'vibed-locator__card-addr-line';
      var line2 = document.createElement('span');
      line2.textContent = [s.city, s.state].filter(Boolean).join(', ') + (s.zip ? ' ' + s.zip : '');
      line2.className = 'vibed-locator__card-addr-line';
      addrEl.appendChild(line1);
      addrEl.appendChild(line2);

      var n = (s.skus || []).length;
      node.querySelector('.vibed-locator__card-products-count').textContent =
        n + (n === 1 ? ' Flavor' : ' Flavors');

      var link = node.querySelector('.vibed-locator__card-dir');
      link.href = directionsUrl(s);

      // Tapping the "N Flavors" count opens the same detail view as clicking
      // the card -- it's the affordance for "tell me what they actually have".
      var prodBtn = node.querySelector('.vibed-locator__card-products');
      prodBtn.addEventListener('click', function (e) {
        e.stopPropagation();
        showDetail(s);
      });

      li.addEventListener('click', function (e) {
        if (e.target.closest('.vibed-locator__card-dir')) return;
        if (e.target.closest('.vibed-locator__card-products')) return;
        showDetail(s);
      });
      listEl.appendChild(node);
      visibleCount++;
    });

    emptyEl.hidden = visibleCount > 0;
    updateMarkerVisibility();
  }

  function updateMarkerVisibility() {
    markers.forEach(function (m) {
      m.marker.setVisible(passesAll(m.store));
    });
  }

  function richInfoWindowHtml(s) {
    var initial = avatarInitial(s.name);
    var line1 = s.address;
    var line2 = [s.city, s.state].filter(Boolean).join(', ') + (s.zip ? ' ' + s.zip : '');
    var n = (s.skus || []).length;
    var prodLabel = n + (n === 1 ? ' Flavor' : ' Flavors');
    return (
      '<div class="vibed-locator__iw">' +
        '<button type="button" class="vibed-locator__iw-close" aria-label="Close" data-iw-close>&times;</button>' +
        '<div class="vibed-locator__iw-row">' +
          '<div class="vibed-locator__iw-avatar">' + escapeHtml(initial) + '</div>' +
          '<div class="vibed-locator__iw-body">' +
            '<div class="vibed-locator__iw-name">' + escapeHtml(s.name) + '</div>' +
            '<div class="vibed-locator__iw-addr">' +
              '<div>' + escapeHtml(line1) + '</div>' +
              '<div>' + escapeHtml(line2) + '</div>' +
            '</div>' +
            '<div class="vibed-locator__iw-meta">' +
              '<span>' + escapeHtml(prodLabel) + '</span>' +
              '<a target="_blank" rel="noopener" href="' + directionsUrl(s) + '">Directions →</a>' +
            '</div>' +
          '</div>' +
        '</div>' +
      '</div>'
    );
  }

  function showDetail(s, opts) {
    if (!s) return;
    opts = opts || {};
    // Save the prior map view on the FIRST entry into detail mode so
    // hitting Back returns to whatever zoom/center the user was at.
    if (!selectedStoreId) {
      var c = map.getCenter();
      prevMapState = { center: { lat: c.lat(), lng: c.lng() }, zoom: map.getZoom() };
    }
    selectedStoreId = s.id;

    // Pan + zoom map to the store, drop the rich info window on the marker.
    var entry = markers.find(function (m) { return m.store.id === s.id; });
    if (entry) {
      map.panTo(entry.marker.getPosition());
      if (map.getZoom() < 15) map.setZoom(15);
      // Nudge the map down a bit so the info window (which opens above the
      // marker) doesn't push the marker off the visible area on mobile.
      // Using setTimeout because setZoom triggers a repaint that resets
      // the panBy mid-animation otherwise.
      if (isMobile()) {
        setTimeout(function () {
          var h = mapEl.offsetHeight || 0;
          if (h) map.panBy(0, -h * 0.18);
        }, 80);
      }
      infoWindow.setContent(richInfoWindowHtml(s));
      infoWindow.open(map, entry.marker);
    }

    // Populate the left-panel detail view.
    document.getElementById('vibed-locator-detail-name').textContent = s.name;
    var addrEl = document.getElementById('vibed-locator-detail-addr');
    addrEl.textContent = '';
    var l1 = document.createElement('div');
    l1.textContent = s.address;
    var l2 = document.createElement('div');
    l2.textContent = [s.city, s.state].filter(Boolean).join(', ') + (s.zip ? ' ' + s.zip : '');
    addrEl.appendChild(l1);
    addrEl.appendChild(l2);

    document.getElementById('vibed-locator-detail-avatar').textContent = avatarInitial(s.name);

    var distEl = document.getElementById('vibed-locator-detail-distance');
    if (origin) {
      var d = haversine(origin, { lat: s.lat, lng: s.lng });
      distEl.querySelector('span').textContent = formatDistance(d);
      distEl.style.display = '';
    } else {
      distEl.style.display = 'none';
    }

    var n = (s.skus || []).length;
    document.getElementById('vibed-locator-detail-products').textContent =
      n + (n === 1 ? ' Flavor' : ' Flavors');

    document.getElementById('vibed-locator-detail-dir').href = directionsUrl(s);

    // Build the per-flavor product cards.
    var productListEl = document.getElementById('vibed-locator-detail-product-list');
    productListEl.innerHTML = '';
    var prodTpl = $('vibed-locator-product-tpl');
    (s.skus || []).forEach(function (sku) {
      var node = prodTpl.content.cloneNode(true);
      var img = node.querySelector('.vibed-locator__product-img');
      img.src = cfg.skuImages[sku] || '';
      img.alt = cfg.skuLabels[sku] || sku;
      node.querySelector('.vibed-locator__product-name').textContent = cfg.skuLabels[sku] || sku;
      productListEl.appendChild(node);
    });

    paneEl.classList.add('is-detail');
    detailEl.hidden = false;

    // On mobile, scroll the detail panel into view ONLY when the trigger
    // was a list-card tap (the user was already in the list area).
    // Marker taps stay on the map -- the user clicked the map to look at
    // the map, not to be scrolled away from it.
    if (isMobile() && opts.source !== 'marker') {
      var paneRect = paneEl.getBoundingClientRect();
      if (paneRect.top > window.innerHeight * 0.5) {
        window.scrollTo({
          top: window.scrollY + paneRect.top - window.innerHeight * 0.4,
          behavior: 'smooth'
        });
      }
    }
  }

  function hideDetail() {
    selectedStoreId = null;
    paneEl.classList.remove('is-detail');
    detailEl.hidden = true;
    if (infoWindow) infoWindow.close();
    if (prevMapState) {
      map.setCenter(prevMapState.center);
      map.setZoom(prevMapState.zoom);
      prevMapState = null;
    }
  }

  function buildMap() {
    map = new google.maps.Map(mapEl, {
      center: cfg.defaultCenter,
      zoom: cfg.defaultZoom,
      mapTypeControl: false,
      streetViewControl: false,
      fullscreenControl: false,
      gestureHandling: 'greedy',
      styles: [
        { featureType: 'poi', stylers: [{ visibility: 'off' }] },
        { featureType: 'transit', stylers: [{ visibility: 'off' }] }
      ]
    });
    // headerDisabled hides Google's default header (and its close button)
    // so our custom X in richInfoWindowHtml is the only one shown.
    infoWindow = new google.maps.InfoWindow({ headerDisabled: true });
    geocoder = new google.maps.Geocoder();

    // Custom close-button delegation. Re-runs every time content updates.
    document.addEventListener('click', function (e) {
      if (e.target.closest('[data-iw-close]')) {
        e.preventDefault();
        infoWindow.close();
      }
    });
  }

  function placeMarkers() {
    markers.forEach(function (m) { m.marker.setMap(null); });
    markers = [];
    stores.forEach(function (s) {
      if (!s.lat || !s.lng) return;
      var marker = new google.maps.Marker({
        position: { lat: s.lat, lng: s.lng },
        map: map,
        title: s.name,
        icon: {
          path: google.maps.SymbolPath.CIRCLE,
          scale: 7,
          fillColor: '#FFCA45',
          fillOpacity: 1,
          strokeColor: '#302E2C',
          strokeWeight: 1.5
        }
      });
      marker.addListener('click', function () {
        showDetail(s, { source: 'marker' });
      });
      markers.push({ id: s.id, marker: marker, store: s });
    });
  }

  function escapeHtml(s) {
    return String(s || '').replace(/[&<>"']/g, function (c) {
      return { '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' }[c];
    });
  }

  function fitBoundsToMatches() {
    var visible = markers.filter(function (m) { return passesAll(m.store); });
    if (!visible.length) return;
    var bounds = new google.maps.LatLngBounds();
    visible.forEach(function (m) { bounds.extend(m.marker.getPosition()); });
    map.fitBounds(bounds, 40);
  }

  function handleSearch(e) {
    e.preventDefault();
    if (selectedStoreId) hideDetail();
    var q = $('vibed-locator-zip').value.trim();
    if (!q) {
      origin = null;
      contextEl.textContent = 'Showing all retailers';
      renderList();
      fitBoundsToMatches();
      return;
    }
    geocoder.geocode({
      address: q,
      componentRestrictions: { country: 'US' }
    }, function (results, status) {
      if (status === 'OK' && results[0]) {
        var loc = results[0].geometry.location;
        origin = { lat: loc.lat(), lng: loc.lng() };
        contextEl.textContent = 'Closest to ' + (results[0].formatted_address || q);
        renderList();
        map.setCenter(origin);
        map.setZoom(11);
      } else {
        contextEl.textContent = 'Could not find "' + q + '". Try a ZIP code.';
      }
    });
  }

  function handleGeo() {
    if (!navigator.geolocation) {
      contextEl.textContent = 'Browser does not support geolocation';
      return;
    }
    contextEl.textContent = 'Locating...';
    navigator.geolocation.getCurrentPosition(function (pos) {
      origin = { lat: pos.coords.latitude, lng: pos.coords.longitude };
      contextEl.textContent = 'Closest to your location';
      renderList();
      map.setCenter(origin);
      map.setZoom(11);
    }, function () {
      contextEl.textContent = 'Could not access your location';
    });
  }

  // Silent auto-locate fired on page load. Resolves to coords or null
  // (denied/timeout/unsupported). Swallows errors -- if the user blocks,
  // we fall back to fit-bounds and they can still click the geo button.
  function tryAutoLocate() {
    return new Promise(function (resolve) {
      if (!navigator.geolocation) return resolve(null);
      var settled = false;
      var done = function (val) { if (!settled) { settled = true; resolve(val); } };
      // Belt + suspenders: navigator's own timeout doesn't always fire on
      // some browsers when permission is dismissed (vs explicitly denied).
      setTimeout(function () { done(null); }, 9000);
      navigator.geolocation.getCurrentPosition(
        function (pos) { done({ lat: pos.coords.latitude, lng: pos.coords.longitude }); },
        function () { done(null); },
        { timeout: 8000, maximumAge: 600000 }
      );
    });
  }

  function bindFilters() {
    var chips = document.querySelectorAll('.vibed-locator__chip');
    var allChip = document.querySelector('.vibed-locator__chip[data-flavor="all"]');
    chips.forEach(function (chip) {
      chip.addEventListener('click', function () {
        if (selectedStoreId) hideDetail();
        var flavor = chip.dataset.flavor;
        if (flavor === 'all') {
          activeFlavors.clear();
        } else {
          if (activeFlavors.has(flavor)) {
            activeFlavors.delete(flavor);
          } else {
            activeFlavors.add(flavor);
          }
        }
        // Sync chip states with the set
        chips.forEach(function (c) {
          var f = c.dataset.flavor;
          if (f === 'all') {
            c.classList.toggle('is-active', activeFlavors.size === 0);
          } else {
            c.classList.toggle('is-active', activeFlavors.has(f));
          }
        });
        renderList();
        // Filter never changes the map view -- if the user has zoomed
        // into a ZIP/area, narrowing flavors should keep them there.
      });
    });
  }

  function fetchFeed() {
    if (!cfg.feedUrl) {
      contextEl.textContent = 'Locator not configured (missing feed URL)';
      return Promise.resolve(null);
    }
    return fetch(cfg.feedUrl, { cache: 'no-store' })
      .then(function (r) {
        if (!r.ok) throw new Error('Feed HTTP ' + r.status);
        return r.json();
      })
      .catch(function (err) {
        console.error('[VibedLocator] feed error', err);
        contextEl.textContent = 'Could not load store list';
        return null;
      });
  }

  function init() {
    mapEl = $('vibed-locator-map');
    listEl = $('vibed-locator-results');
    contextEl = $('vibed-locator-context');
    emptyEl = $('vibed-locator-empty');
    paneEl = $('vibed-locator-pane');
    detailEl = $('vibed-locator-detail');
    if (!mapEl || !listEl) return;

    bindFilters();
    $('vibed-locator-form').addEventListener('submit', handleSearch);
    $('vibed-locator-geo').addEventListener('click', handleGeo);
    $('vibed-locator-back').addEventListener('click', hideDetail);
  }

  function bootMap() {
    buildMap();
    // Race feed fetch and geolocation in parallel so first paint isn't
    // delayed by either one. If geolocation succeeds we center on the
    // user; if it fails or is denied we fit bounds to all markers.
    Promise.all([fetchFeed(), tryAutoLocate()]).then(function (results) {
      var data = results[0];
      var coords = results[1];
      if (!data) return;
      stores = (data.stores || []).filter(function (s) { return s.lat && s.lng; });
      placeMarkers();
      if (coords) {
        origin = coords;
        contextEl.textContent = 'Closest to your location';
        renderList();
        map.setCenter(origin);
        map.setZoom(11);
      } else {
        renderList();
        fitBoundsToMatches();
      }
    });
  }

  ready(init);
  // Google Maps will call this once its script loads. If Maps loaded
  // first (and our placeholder set the flag), boot immediately.
  var mapsAlreadyReady = window.__vibedLocatorMapsReady === true;
  window.VibedLocatorBoot = bootMap;
  if (mapsAlreadyReady) {
    ready(bootMap);
  }
})();
